import Pubnub from 'pubnub';
import type {
  UrlUnfurled,
  UrlUnfurledData,
  UrlUnfurledStructuredData,
} from '../types/urlUnfurled';

function parseStructuredDataStringField(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  return null;
}

function parsePreferredOrder(value: unknown): number | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) {
      return undefined;
    }
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) {
      return undefined;
    }
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return undefined;
    }
    return parsed;
  }
  return undefined;
}

function firstPreferredOrderFromSources(
  ...sources: unknown[]
): number | undefined {
  for (let i = 0; i < sources.length; i += 1) {
    const parsed = parsePreferredOrder(sources[i]);
    if (parsed !== undefined) {
      return parsed;
    }
  }
  return undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/** History / JSON:API: `id` + `attributes.unfurledUrl` object (preferredOrder optional on attributes). */
function isJsonApiUrlUnfurledResource(
  candidate: Record<string, unknown>,
): boolean {
  const attrs = candidate.attributes;
  if (!isPlainObject(attrs)) {
    return false;
  }
  return isPlainObject(attrs.unfurledUrl);
}

/** Legacy flat real-time: required ids on the same object as nested `unfurledUrl.url`. */
function isLegacyFlatUrlUnfurledResource(
  candidate: Record<string, unknown>,
): boolean {
  if (typeof candidate.originalMessageId !== 'string') {
    return false;
  }
  if (typeof candidate.sessionId !== 'string') {
    return false;
  }
  if (!isPlainObject(candidate.unfurledUrl)) {
    return false;
  }
  return typeof candidate.unfurledUrl.url === 'string';
}

/**
 * Resolves the url_unfurled **resource** object from heterogeneous PubNub envelopes.
 *
 * Real-time from chat-api usually nests the body under **`payload.data`**. Some envelopes also include a
 * **`message.data.attributes`** object (often empty or non-unfurl); treating any truthy `attributes` as the
 * JSON:API resource incorrectly skipped **`payload.data`** and broke legacy flat real-time (incl. when
 * `preferredOrder` is omitted). Order: `payload.data` → JSON:API `data` → nested `data.data` → legacy flat `data`.
 */
function pickUrlUnfurledResource(
  messageAny: Record<string, unknown>,
): Record<string, unknown> | null {
  const { payload, data: topData } = messageAny;
  const payloadData = isPlainObject(payload)
    ? (payload as Record<string, unknown>).data
    : undefined;
  if (isPlainObject(payloadData)) {
    return payloadData;
  }

  if (!isPlainObject(topData)) {
    return null;
  }

  if (isJsonApiUrlUnfurledResource(topData)) {
    return topData;
  }

  const nested = topData.data;
  if (isPlainObject(nested)) {
    return nested;
  }

  if (isLegacyFlatUrlUnfurledResource(topData)) {
    return topData;
  }

  return null;
}

/**
 * Dependencies interface for parseUrlUnfurled function
 * Supports dependency injection for testing and customization
 */
interface ParseUrlUnfurledDependencies {
  /** Function to log errors during parsing */
  logError: (message: string, error?: unknown) => void;
}

/**
 * Default dependencies for parseUrlUnfurled function
 * Uses console.error for error logging
 */
const defaultParseUrlUnfurledDeps: ParseUrlUnfurledDependencies = {
  logError: (message, error) => console.error(message, error),
};

/**
 * Parse URL unfurled data from PubNub message
 *
 * Handles PubNub message shapes for `url_unfurled`:
 * - **JSON:API resource** (`type` + `id` + `attributes.originalMessageId` / `sessionId` / `unfurledUrl` / optional `preferredOrder`):
 *   - History: `message.data` is the resource
 *   - Real-time: prefer **`message.payload.data`**, then `message.data` when it has `attributes.unfurledUrl`, then `message.data.data` (matches chat-api `createUnfurledUrlEventBody`)
 * - **Legacy flat real-time** (`id`, `originalMessageId`, `sessionId`, `unfurledUrl` on the same object): prefer **`message.payload.data`**, then `message.data.data`, then top-level `message.data`
 *
 * Extracts the following fields:
 * - id: Unique identifier for the URL unfurled event
 * - originalMessageId: ID of the message that contained the URL
 * - sessionId: Conversation/session ID
 * - unfurledUrl: Object containing url, title, description, image, favicon, unfurledAt, optional structuredData; **`preferredOrder`** may appear on **`attributes`** (chat-api JSON API) or on **`unfurledUrl`** (legacy/tests)
 *
 * @param message - PubNub message containing URL unfurled event (either real-time or history format)
 * @param deps - Optional dependencies object for dependency injection (defaults to real implementations)
 * @returns Parsed UrlUnfurled object with conversationId set from sessionId, or null if parsing fails
 *
 * @example
 * ```typescript
 * const urlUnfurled = parseUrlUnfurled(pubnubMessage);
 * if (urlUnfurled) {
 *   console.log('Preview URL:', urlUnfurled.unfurledUrl.url);
 * }
 * ```
 */
export function parseUrlUnfurled(
  message: Pubnub.MessageEvent['message'],
  deps: Partial<ParseUrlUnfurledDependencies> = {},
): UrlUnfurled | null {
  const { logError = defaultParseUrlUnfurledDeps.logError } = deps;

  try {
    const messageAny = message as Record<string, unknown>;
    const resource = pickUrlUnfurledResource(messageAny);

    if (!resource) {
      logError(
        'Invalid URL unfurled message format: missing url_unfurled resource (expected payload.data, data.attributes.unfurledUrl, data.data, or legacy flat data)',
        message,
      );
      return null;
    }

    const attrs = resource.attributes as Record<string, unknown> | undefined;
    const hasJsonApiAttributes =
      attrs != null &&
      typeof attrs === 'object' &&
      attrs.unfurledUrl != null &&
      typeof attrs.unfurledUrl === 'object';

    let id: string;
    let originalMessageId: string;
    let sessionId: string;
    let unfurledUrl: Record<string, unknown>;
    let historyAttributesPreferred: unknown | undefined;
    let nestedAttributesPreferred: unknown | undefined;
    let envelopePreferred: unknown | undefined;

    if (hasJsonApiAttributes) {
      const a = attrs as Record<string, unknown>;
      id = resource.id as string;
      originalMessageId = a.originalMessageId as string;
      sessionId = a.sessionId as string;
      unfurledUrl = a.unfurledUrl as Record<string, unknown>;
      historyAttributesPreferred = a.preferredOrder;
    } else {
      const envelope = resource;
      id = resource.id as string;
      originalMessageId = resource.originalMessageId as string;
      sessionId = resource.sessionId as string;
      unfurledUrl = resource.unfurledUrl as Record<string, unknown>;
      envelopePreferred = envelope.preferredOrder;
      const nestedAttrs = envelope.attributes;
      nestedAttributesPreferred =
        nestedAttrs != null && typeof nestedAttrs === 'object'
          ? (nestedAttrs as Record<string, unknown>).preferredOrder
          : undefined;
    }

    if (!id || !originalMessageId || !sessionId || !unfurledUrl) {
      logError('Invalid URL unfurled message: missing required fields', {
        id,
        originalMessageId,
        sessionId,
        unfurledUrl,
      });
      return null;
    }

    if (!unfurledUrl.url || typeof unfurledUrl.url !== 'string') {
      logError('Invalid URL unfurled message: missing unfurledUrl.url', {
        unfurledUrl,
      });
      return null;
    }

    const uvRaw = unfurledUrl as Record<string, unknown>;
    const nestedStructured = uvRaw.structuredData;
    const structuredData: UrlUnfurledStructuredData | undefined =
      nestedStructured != null && typeof nestedStructured === 'object'
        ? {
            title: parseStructuredDataStringField(
              (nestedStructured as Record<string, unknown>).title,
            ),
            price: parseStructuredDataStringField(
              (nestedStructured as Record<string, unknown>).price,
            ),
          }
        : undefined;

    const preferredOrder = hasJsonApiAttributes
      ? firstPreferredOrderFromSources(
          historyAttributesPreferred,
          uvRaw.preferredOrder,
        )
      : firstPreferredOrderFromSources(
          nestedAttributesPreferred,
          envelopePreferred,
          uvRaw.preferredOrder,
        );

    const unfurledUrlData: UrlUnfurledData = {
      url: unfurledUrl.url as string,
      title: unfurledUrl.title as string | undefined,
      description: unfurledUrl.description as string | undefined,
      image: unfurledUrl.image as string | undefined,
      favicon: unfurledUrl.favicon as string | undefined,
      unfurledAt: unfurledUrl.unfurledAt as string | undefined,
    };
    if (structuredData !== undefined) {
      unfurledUrlData.structuredData = structuredData;
    }
    if (preferredOrder !== undefined) {
      unfurledUrlData.preferredOrder = preferredOrder;
    }

    return {
      id,
      originalMessageId,
      sessionId,
      unfurledUrl: unfurledUrlData,
      conversationId: sessionId,
    };
  } catch (error) {
    logError('Failed to parse URL unfurled message', error);
    return null;
  }
}
